import axios from 'axios';
import Swal from 'sweetalert2';

const apiUrl = process.env.REACT_APP_API_URL;

let authToken = localStorage.getItem('accessToken') ? localStorage.getItem('accessToken') : null
let isRefreshing = false;
let failedQueue = [];
let refreshRequest = null;

const axiosInstance = axios.create({
  baseURL: apiUrl,
  headers: {
    Authorization: authToken ? `Bearer ${authToken}` : undefined,
    'Content-Type': 'application/json',
  },
});

// Request Interceptor
axiosInstance.interceptors.request.use(
  (req) => {
    // Always get fresh token from localStorage
    const token = localStorage.getItem('accessToken');
    if (token) {
      authToken = token;
      req.headers.Authorization = `Bearer ${token}`;
    } else {
      delete req.headers.Authorization;
      authToken = null;
    }
    return req;
  },
  (error) => Promise.reject(error)
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const isLogoutRequest = error.config?.url?.includes('/logout');
    const isRefreshRequest = error.config?.url?.includes('/refreshToken');

    if (error.response && error.response.status === 401) {
      // Don't try to refresh if already on a refresh or logout request
      if (isLogoutRequest || isRefreshRequest) {
        handleSessionExpired();
        return Promise.reject({
          statusCode: 401,
          message: 'Unauthorized',
        });
      }

      // If not currently refreshing, start a refresh attempt
      if (!isRefreshing) {
        isRefreshing = true;
        try {
          const refreshResponse = await refreshTokenAndRetry(error);
          isRefreshing = false;
          failedQueue = [];
          return refreshResponse;
        } catch (refreshError) {
          isRefreshing = false;
          failedQueue = [];
          return Promise.reject(refreshError);
        }
      } else {
        // Queue other requests while refresh is in progress
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          return axiosInstance.request(error.response.config);
        }).catch((queueError) => {
          return Promise.reject(queueError);
        });
      }
    } else if (error.response && (error.response.status === 403 || error.response.status === 422)) {
      // Handle 403 Forbidden or 422 (token invalidated)
      if (!isLogoutRequest && !isRefreshRequest) {
        console.log('Forbidden/Unprocessable error, clearing session');
        handleSessionExpired();
      }
      const result = {
        statusCode: error.response?.data?.statusCode || error.response?.status || 500,
        message: error.response?.data?.message || 'Session invalidated',
      };
      return Promise.reject(result);
    } else {
      const result = {
        statusCode: error.response?.data?.statusCode || error.response?.status || 500,
        message: error.response?.data?.message || 'Unknown error',
      };
      if (error.response?.data?.statusList) {
        result.statusList = error.response.data.statusList;
      }
      return Promise.reject(result);
    }
  }
);

async function refreshTokenAndRetry(error) {
  try {
    // If we already have a pending refresh request, wait for it
    if (refreshRequest) {
      await refreshRequest;
    } else {
      // Start new refresh request
      refreshRequest = refreshToken(error);
      await refreshRequest;
      refreshRequest = null;
    }

    // Process queued requests
    const queue = [...failedQueue];
    queue.forEach(({ resolve }) => {
      resolve();
    });

    // Retry the original request with new token
    return axiosInstance.request(error.response.config);
  } catch (refreshError) {
    // Process queued requests with error
    const queue = [...failedQueue];
    queue.forEach(({ reject }) => {
      reject(refreshError);
    });
    refreshRequest = null;
    return Promise.reject(refreshError);
  }
}

async function refreshToken(error) {
  try {
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (!storedRefreshToken) {
      handleSessionExpired();
      throw new Error('No refresh token available');
    }

    // Use axios directly to avoid interceptors loop
    const response = await axios.post(`${apiUrl}api/v1/ekutir/user/auth/refreshToken`, {
      refreshToken: storedRefreshToken
    });

    const { accessToken, refreshToken: newRefreshToken, user } = response.data;
    if (accessToken && newRefreshToken) {
      // Update localStorage
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', newRefreshToken);
      if (user) {
        localStorage.setItem('userData', JSON.stringify(user));
      }
      // Update the module-level authToken variable
      authToken = accessToken;
      // Update the original request config with new token
      error.response.config.headers['Authorization'] = `Bearer ${accessToken}`;
    } else {
      handleSessionExpired();
      throw new Error('Refresh response incomplete');
    }
  } catch (err) {
    handleSessionExpired();
    throw err;
  }
}


function handleSessionExpired() {
  // Clear all auth-related data
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("accessToken");
  localStorage.removeItem("userData");
  sessionStorage.clear();
  authToken = null;
  failedQueue = [];
  refreshRequest = null;

  Swal.fire({
    text: 'Session Expired',
    confirmButtonText: 'Ok',
    customClass: {
      popup: 'customSwal',
    },
  }).then((result) => {
    if (result.isConfirmed) {
      window.location.href = '/auth/login';
    }
  });
  throw new Error('Session expired');
}

export default axiosInstance;